"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { FormattedMessage } from "react-intl"

import { Box, P, PressableBox } from "@bullseye/components"

import { DiscoveryTooltipProps } from "."

export default function DiscoveryTooltip({
  visible,
  anchorRef,
  onCtaPress,
}: DiscoveryTooltipProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    if (!visible || !anchorRef.current) {
      setPos(null)
      return
    }

    const updatePos = () => {
      const el = anchorRef.current as unknown as HTMLDivElement | null
      if (!el || el.offsetParent === null) {
        setPos(null)
        return
      }
      const rect = el.getBoundingClientRect()
      if (rect) {
        setPos({
          top: rect.bottom + 4,
          left: rect.left + rect.width / 2,
        })
      }
    }

    requestAnimationFrame(updatePos)
    const settleTimeout = setTimeout(updatePos, 500)

    window.addEventListener("scroll", updatePos, true)
    window.addEventListener("resize", updatePos)
    return () => {
      clearTimeout(settleTimeout)
      window.removeEventListener("scroll", updatePos, true)
      window.removeEventListener("resize", updatePos)
    }
  }, [visible, anchorRef])

  if (!visible || !pos) return null

  return createPortal(
    <Box
      style={
        {
          position: "fixed",
          top: pos.top,
          left: pos.left,
          transform: "translateX(-129px)",
          width: 258,
          zIndex: 9999,
        } as unknown as Record<string, unknown>
      }
    >
      <Box
        className="absolute -top-[6px] left-1/2 z-20 -translate-x-1/2 rotate-45 bg-white"
        style={{ width: 11, height: 11 }}
      />
      <Box
        className="relative z-10 bg-white"
        style={
          {
            borderRadius: 15,
            padding: 15,
            boxShadow: "0 0 50px -12px rgba(0,0,0,0.25)",
          } as unknown as Record<string, unknown>
        }
      >
        <P bold className="text-[16px] leading-[19px] text-[#26222f]">
          <FormattedMessage id="filters.category.discovery.title" />
        </P>
        <P className="mt-[5px] text-[13px] font-medium leading-4 text-[#8a8097]">
          <FormattedMessage id="filters.category.discovery.description" />
        </P>
        <PressableBox onPress={onCtaPress} className="mt-2 self-end">
          <P bold className="text-[13px] leading-4 text-[#8b4fe3]">
            <FormattedMessage id="filters.category.discovery.cta" />
          </P>
        </PressableBox>
      </Box>
    </Box>,
    document.body,
  )
}
